/**
 * The per-match drain lock, over the Web Locks API.
 *
 * The page and the service worker can both drain the same match, and both must be able to
 * without either knowing about the other. `navigator.locks` is shared across every context on
 * the origin, which is exactly what is needed: whoever holds `sportsos-drain:<match>` is the
 * drainer, and everyone else skips that match for now.
 *
 * `ifAvailable` is the point. A drain can hold the lock for minutes while it backs off, and a
 * second drainer queueing behind it would achieve nothing but a pile of waiters.
 *
 * What it protects is the on-disk renumbering after a `seq_conflict`, where two drainers
 * rewriting the same event would race. It is **not** what prevents duplicate balls on the
 * server — that is `eventId` dedupe there, and it must not be removed on the grounds that a
 * lock exists.
 */
import type { LockRunner } from "@sportsos/sync";

/** The slice of `LockManager` this uses. An interface so tests need no browser. */
export interface LockLike {
  request<T>(
    name: string,
    options: { readonly ifAvailable: true },
    callback: (lock: object | null) => Promise<T>,
  ): Promise<T>;
}

export function lockRunner(locks: LockLike | undefined): LockRunner {
  return async (key, run) => {
    // A browser with no Web Locks (old WebViews) drains unguarded. Safe, because the server
    // dedupes; it only forgoes the protection against a concurrent renumber.
    if (locks === undefined) {
      await run();
      return true;
    }
    return locks.request(`sportsos-drain:${key}`, { ifAvailable: true }, async (lock) => {
      if (lock === null) return false;
      await run();
      return true;
    });
  };
}

/** The platform's lock manager, in a window or a worker, or undefined where there is none. */
export const platformLocks = (): LockLike | undefined =>
  typeof navigator === "undefined"
    ? undefined
    : (navigator as unknown as { locks?: LockLike }).locks;
